import { useTranslation } from 'react-i18next';
import { useCart } from 'context/CartContext';
import formatIDR from 'utils/formatIDR';
import Notice, { NoticeAction } from './Notice';

/**
 * What the shopper should know before paying, from the server's validation:
 * lines that cannot be sold as they are (the lines themselves say which and
 * offer the fix), changed prices (kept until acknowledged, then the cart
 * remembers the new price), and a validation that could not run.
 */
export default function CartNotices({ validation, stockMessage = false }) {
  const { t } = useTranslation();
  const { lines, reconcile, remove } = useCart();
  const data = validation.data;
  const blocking = data ? data.blockingVariantIds.length : 0;
  const changes = data ? data.priceChanges : [];
  const marked = data ? data.blockingVariantIds.map((id) => data.lines[id]) : [];
  const canFix = marked.some((l) => l.issues.includes('QUANTITY_EXCEEDS_STOCK') && l.maxQuantity > 0);
  const gone = data ? data.blockingVariantIds.filter((id) => !data.lines[id].issues.includes('QUANTITY_EXCEEDS_STOCK')) : [];

  if (!stockMessage && !blocking && !changes.length) return null;

  return (
    <div className="space-y-3">
      {stockMessage && (
        <Notice tone="warning" title={t('cart.stockChangedTitle')}>
          {t('cart.stockChangedBody')}
        </Notice>
      )}
      {blocking > 0 && (
        <Notice
          tone="warning"
          title={t('cart.blockingTitle', { count: blocking })}
          actions={
            <>
              {canFix && (
                <NoticeAction onClick={() => reconcile(data.serverLines, { fixQuantities: true })}>
                  {t('cart.fixAll')}
                </NoticeAction>
              )}
              {gone.length > 0 && (
                <NoticeAction onClick={() => gone.forEach(remove)}>{t('cart.removeUnavailable')}</NoticeAction>
              )}
            </>
          }
        >
          {t('cart.blockingBody')}
        </Notice>
      )}
      {changes.length > 0 && (
        <Notice
          tone="info"
          title={t('cart.priceChangedTitle', { count: changes.length })}
          actions={<NoticeAction onClick={() => reconcile(data.serverLines)}>{t('cart.priceChangedOk')}</NoticeAction>}
        >
          <ul className="space-y-1">
            {changes.map((c) => {
              const line = lines.find((l) => l.variantId === c.variantId);
              return (
                <li key={c.variantId}>
                  {t(c.to > c.from ? 'cart.priceUpLine' : 'cart.priceDownLine', {
                    name: line ? line.name : '',
                    from: formatIDR(c.from),
                    to: formatIDR(c.to),
                  })}
                </li>
              );
            })}
          </ul>
        </Notice>
      )}
    </div>
  );
}
